import React, { forwardRef, type FC } from 'react';
import { styled } from '@compiled/react';
import { type SizeType, BORDER_WIDTH, AVATAR_SIZES } from '@atlaskit/avatar';

import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { OVERLAP_PIXELS, Z_INDEX } from './constants.tsx';

export const hiddenCheckboxStyles = {
	position: 'absolute',
	width: '100%',
	height: '100%',
	padding: 0,
	border: 0,
	opacity: 0,
	overflow: 'hidden',
	'white-space': 'nowrap',
	cursor: 'pointer',
};

type AvatarWrapperProps = {
	selected?: boolean;
	zIndex: number;
	jiggle?: boolean;
	isTanukiSpaceTokenFix?: boolean;
	ref?: React.Ref<HTMLDivElement>;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AvatarWrapperBase = styled.div<AvatarWrapperProps>({
	display: 'inline-flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	position: 'relative',
	margin: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: (props: AvatarWrapperProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		props.isTanukiSpaceTokenFix ? token('space.negative.100') : `-${OVERLAP_PIXELS}px`,
	background: 'none',
	verticalAlign: 'top',
	padding: 0,
	transition: 'transform 0.1s ease-out',
	borderRadius: '50%',
	border: 0,
	outline: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	zIndex: (props: AvatarWrapperProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		props.selected ? props.zIndex + Z_INDEX.SELECTED : props.zIndex,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		marginLeft: 0,
	},
	'&:hover, &:focus, &:focus-within': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		transform: `translateY(${(props: AvatarWrapperProps) => (props.jiggle ? -gridSize / 2 : 0)}px)`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		zIndex: (props: AvatarWrapperProps) => props.zIndex + Z_INDEX.HOVER,
		'&::after': {
			position: 'absolute',
			content: "''",
			width: '100%',
			top: 0,
			left: '-1px',
			right: '-1px',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			bottom: `-${(props: AvatarWrapperProps) => (props.jiggle ? gridSize / 2 : 0)}px`,
			zIndex: -1,
			transform: 'translateY(4px)',
			cursor: 'pointer',
		},
	},
});

const AvatarWrapperBaseWithRef: FC<AvatarWrapperProps> = forwardRef((props, ref) => {
	return <AvatarWrapperBase {...props} isTanukiSpaceTokenFix ref={ref} />;
});

const AvatarWrapperOld = componentWithFG(
	'tanuki_padding_margin_use_token',
	AvatarWrapperBaseWithRef,
	AvatarWrapperBase,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AvatarWrapperNew = styled(AvatarWrapperOld)({
	height: 'unset',
	border: 0,
	padding: 0,
	borderRadius: '50%',
	background: 'none',
	outline: 0,
});

export const AvatarWrapper = componentWithCondition(
	isVisualRefreshEnabled,
	AvatarWrapperNew,
	AvatarWrapperOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ActionAvatar = styled.button<{ size?: SizeType }>({
	margin: 0,
	padding: 0,
	borderRadius: '50%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderWidth: `${BORDER_WIDTH}px`,
	borderStyle: 'solid',

	borderColor: `${token('color.text.inverse')}`,

	backgroundColor: token('color.background.neutral'),
	transition: 'background-color 0.1s ease-out 0s',
	cursor: 'pointer',
	'&:focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		boxShadow: `0 0 0 ${gridSize / 4}px ${token('color.background.neutral.pressed')}`,
		outline: 'none',
	},
	'&:hover': {
		backgroundColor: token('color.background.neutral.hovered'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ActionAvatarSpacer = styled.span({
	borderRadius: '50%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${AVATAR_SIZES.medium}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${AVATAR_SIZES.medium}px`,
	backgroundColor: 'transparent',
	color: token('color.text'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
	lineHeight: `${AVATAR_SIZES.medium}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AvatarBorder = styled.div<{ selected?: boolean; isSoftwareFullTheming?: boolean }>({
	borderRadius: '50%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderWidth: `${BORDER_WIDTH}px`,
	borderStyle: 'solid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderColor: ({ selected, isSoftwareFullTheming }) =>
		// eslint-disable-next-line no-nested-ternary
		selected
			? isSoftwareFullTheming
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
					Tokens.COLOR_BORDER_SELECTED
				: token('color.icon.brand')
			: 'transparent',
});
